<template>
<div>
  <header>
    <div class="heading">
      <p>Aunty Joy Murphy Wandin, Jonathan Jones and Tom Nicholson</p>
      <h1>untitled (seven monuments)</h1>
    </div>

    <figure class="illustration"></figure>
  </header>

  <main>
    <div class="about">
      <p><em>untitled (seven monuments)</em> is an art project by Aunty Joy Murphy Wandin (Wurundjeri), Jonathan Jones (Wiradjuri/Kamilaroi) and Tom Nicholson (Celtic-Australian). Seven monuments mark the historical boundary of Coranderrk Aboriginal Station, one of Australia’s most significant sites.</p>

      <p>Established by Wurundjeri people and other displaced Aboriginal people in 1863, Coranderrk Aboriginal Station quickly becomes both a thriving economic community and a powerful base for Aboriginal self-organisation and political advocacy. Coranderrk is reduced and undermined by the Victorian Colonial Government, ultimately leading to the dispersal and forced removal of the community to Lake Tyers in 1924, leaving only a handful of determined elders. Today Coranderrk continues to be an important living place for Wurundjeri and for the wider Aboriginal community.</p>

      <p><em>untitled (seven monuments)</em> invites you to move between seven sites that together mark the boundaries of Coranderrk at its largest, in 1866 when it was some 4,850 acres. Several sites are located within what is known today as Healesville. Others are outside the town. If you are travelling by car, you can visit three sites within an hour: for example, sites 2, 4 and 5, or sites 1, 3 and 5. Over two hours you can visit sites 1, 2, 3, 4 and 5. Over a day you can potentially visit six of the sites (not all seven sites are publicly accessible). <em>untitled (seven monuments)</em> invites you to learn about Coranderrk’s extraordinary history, and to traverse Wurundjeri country. While on country take your time to enjoy the queep-queep (birds) and the animals, the views and the stories.</p>

      <p>Each marker is an upturned flagpole and brick footing, with a plaque on each face, surrounded by Coranderrk (Christmas bush). When visiting the artwork please take some time to look after the monuments: clear any weeds, water the Coranderrk bush, and please report any damage to <a target="_blank" href="https://www.yarraranges.vic.gov.au">Yarra Ranges Council</a> and <a target="_blank" href="https://www.twma.com.au/">TarraWarra Museum of Art</a>.</p>
      
      <p>A PDF copy of the printed leaflet is available for download <a href="/SevenMonuments.pdf" target="_blank">here</a>.</p>
    </div>

    <a class="ack-trigger" href="#" @click.prevent="ack = true">(Acknowledgements)</a>
  </main>

  <div class="acknowledgements" v-if="ack">
    <div class="acknowledgements-heading">
      <h3></h3>
      <h2>Acknowledgements</h2>
      <h3><a href="#" @click.prevent="ack = false">(Close)</a></h3>
    </div>
    <p><em>untitled (seven monuments)</em> is an artwork by Aunty Joy Murphy Wandin, Jonathan Jones and Tom Nicholson. It was commissioned by TarraWarra Museum of Art, as part of the exhibition <em>Future Memorials</em>, 2013–14, curated by Victoria Lynn, and was realised in 2019 with the support of Yarra Ranges Council, the artwork’s custodian.</p>
    <p>The artists would like to thank the following for their generosity, work and skill towards the realisation of this artwork: TarraWarra Museum of Art, in particular Eva Besen AO and Marc Besen AC, Victoria Lynn and Anthony Fitzpatrick; Kevin Mason; Genevieve O’Callaghan; Ziga Testen; Allan Wandin, Brooke Wandin, Jacqui Wandin, Dave Wandin and Tony D’Abbs; Mick Woiwod; Yarra Ranges Council; Brett Cowan, Managing Director, Southern Rainforest; Patricia Wadsworth from PLAQUES.COM.AU; and all the residents and landowners in Healesville and Badger Creek who supported this project.</p>
    <div class="addresses">
      <address>
        TarraWarra Museum of Art<br />
        313 Healesville-Yarra Glen Road, Healesville
        03 5957 3100<br />
        museum@twma.com.au
      </address>
      <address>
        Yarra Ranges Council<br />
        15 Anderson Street, Lilydale<br />
        1300 368 333<br />
        mail@yarraranges.vic.gov.au
      </address>
    </div>
  </div>

  <section>
    <div v-if="location" class="location">
      <div class="location-heading">
        <h3>Site {{location.num}}</h3>
        <h2 v-html="location.title" />
        <h3><a href="#" @click.prevent="selected = null">(Close)</a></h3>
      </div>
      <p v-html="location.body" />
      <p><a target="_blank" :href="directionsUrl(location)">Get directions</a></p>
    </div>
    <mapbox-map class="map" :locations="locations" :selected="selected" @select="num => selected = num" />

  </section>
</div>
</template>

<script>
import MapboxMap from './components/mapbox-map.vue'

const locations = [{
  num: 1,
  title: 'Everad Park Picnic Area, Maroondah Highway',
  body: 'This monument is located in Everard Park Picnic Area on the banks of the Birrarung (Yarra River). It is 100 metres from the gate as you walk away from the Maroondah Highway, amongst the bushes on the left, adjacent to a majestic manna gum tree. This site marks the eastern corner of Coranderrk.',
  coords: [145.49111111, -37.67916667]
}, {
  num: 2,
  title: 'Corner of Mt Riddell Road and Airlie Road',
  body: 'This monument is located on a road reserve at the conjunction of Mt Riddell Road and Airlie Road, approximately 100 metres from the Maroondah Highway. As you look down the valley with your back to the town, you are looking towards the centre of Coranderrk.',
  coords: [145.51277778, -37.66944444]
}, {
  num: 3,
  title: 'Corner of Don Road and Mt Riddell Road',
  body: 'This monument is located opposite Healesville Cemetery, on the corner of Don Road and Mt Riddell Road. Please use the roadside parking provided for the cemetery and take care when crossing Mt Riddell Road to visit the monument. This site marks the northern corner of Coranderrk.',
  coords: [145.53027778, -37.66111111]
}, {
  num: 4,
  title: 'Roma Avenue',
  body: 'This monument, framed by trees and overlooking paddocks, is located at the end of Roma Avenue. Follow Don Road from Site 3 for 3.3 km, take the turn-off into Garibaldi Street, and then turn right into Roma Avenue and drive to the end. Please be respectful of the monument’s neighbours and take care with parking and driving in this street.',
  coords: [145.54972222, -37.68777778]
}, {
  num: 5,
  title: 'Nyora Road, Mount Toolebewong',
  body: 'This monument, overlooking Coranderrk, sits high in the cool air of Mount Toolebewong and marks the eastern border of Coranderrk. From Don Road take the turn-off to Nyora Road. The marker is on a bend some 850 metres up Nyora Road and located a few metres into the bush on the embankment on the left. The marker can be viewed from the roadside.',
  coords: [145.55916667, -37.70166667]
}, {
  num: 6,
  title: 'Birrarung (Yarra River)',
  body: 'This site marks the southern corner of Coranderrk and is located high on the banks of the Birrarung near Spring Creek, nestled amongst manna gum trees and alongside fertile farming land. It is not publicly accessible.',
  coords: [145.50666667, -37.71286111]
}, {
  num: 7,
  title: 'The junction of Birrarung (Yarra River) and Coranderrk Creek (Badger Creek)',
  body: 'This site is on the junction of the Birrarung and Coranderrk Creek and is accessible by appointment only, through the Wandoon Estate Aboriginal Corporation at wandoonestate@gmail.com. With permission, walk from the historic manager’s house, along Coranderrk Creek, until you meet the Birrarung.',
  coords: [145.50055556, -37.69566667]
}]

export default {
  data: () => ({
    ack: false,
    locations,
    selected: null
  }),
  components: {
    MapboxMap
  },
  methods: {
    directionsUrl({ coords }) {
      return `https://www.google.com/maps?saddr=My+Location&daddr=${coords[1]},${coords[0]}`
    }
  },
  computed: {
    location() {
      return this.locations.find(({num}) => num === this.selected)
    }
  }
}
</script>

<style lang="scss" src="./global.scss"></style>

<style scoped lang="scss">
header {
  width: 100vw;
  min-height: 100vh;
}
main {
  width: 100vw;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: var(--gutter);
  position: relative;
  &:before {
    content: '';
    display: block;
    border-top: 2px solid;
    position: absolute;
    top: 0; left: var(--gutter); right: var(--gutter);
  }
  .ack-trigger {
    position: absolute;
    top: 0; right: 0;
    margin: var(--gutter);
    @media (max-width: 640px) {
      position: relative;
      margin: 0;
    }
  }
}
header {
  padding: var(--gutter);
  background: var(--bg);
  .illustration {
    width: 100%;
    mix-blend-mode: multiply;
    height: 100vh;
    background-image: url('/images/seven-monuments-noline.png?v=2');
    background-size: auto 100%;
    background-position: center top;
    margin-bottom: 5.6rem;

    @media (max-width: 640px) {
      height: 150vw;
    }
  }
  .heading {
    text-align: center;
  }
}
main {
  display: grid;
  grid-gap: var(--gutter);
  padding: var(--gutter);
  background: var(--bg);
  position: relative;
  @media (max-width: 640px) {
    margin-bottom: var(--gutter);
  }
  &:after {
    content: '';
    display: block;
    position: absolute;
    bottom: 0; left: var(--gutter); right: var(--gutter);
    border-top: 2px solid;
  }
  .about {
    grid-column: span 2;
    columns: 2;
    column-gap: var(--gutter);
    @media (max-width: 640px) {
      columns: 1;
      grid-column: span 3;
    }
    p {
      break-inside: avoid;
    }
  }
}

h2 {
  text-align: center;
}

section {
  width: 100vw;
  height: 100vh;
  background: var(--bg);
  position: relative;
}

.location, .acknowledgements {
  background: var(--bg);
  position: absolute;
  top: 0; left: 0;
  z-index: 20;
  padding: var(--gutter);
  width: 100%;
  max-width: 46rem;
  box-shadow: 0.3rem 0.3rem 0.2rem rgba(0, 0, 0, 0.3);
  margin: 4.8rem;
  @media (max-width: 640px) {
    bottom: 0; left: 0;
    top: auto;
    margin: 0;
  }
  &-heading {
    display: flex;
    h3 {
      width: 5em;
    }
    h2 {
      flex: 1;
    }
    h3:last-child {
      text-align: right;
    }
  }
}
.acknowledgements {
  position: fixed;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  bottom: initial;
  @media (max-width: 640px) {
    transform: none;
    bottom: initial; left: 0;
    top: 0;
    margin: 0;
  }
  p, .addresses {
    font-size: 1.2rem;
  }
  .addresses {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: var(--gutter);
  }
}
</style>